/**
 * Copyright (C) 2019 Egylis,
 * Siège social, 83 bis rue de l'Abbé Groult, 75015 Paris - France.
 * All Rights Reserved.
 *
 * E-mail : info@egylis.com
 *
 * This software is the confidential and proprietary information
 * of Egylis. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only
 * in accordance with the terms of the license agreement you
 * entered into with Egylis.
 *
 * Created by calvez on 20/03/19.
 *
 * Version : Generator 2.0.9
 */

/// <reference types="cordova-plugin-file" />
/// <reference types="cordova-plugin-file-transfer" />
/// <reference types="cordova-plugin-device" />
/// <reference types="cordova-plugin-camera" />
/// <reference types="cordova-plugin-network-information" />


import {Config, FileData, FileJournal, FileOrigin, FileStatus, FileType, FileUploadStatus, Journal} from '../msa/entity';
import {Injectable} from '@angular/core';
import {ConfigService} from './configService';
import {UtilMpaFunction} from '../mpa/utility';
import {format} from 'date-fns';
import {StatusEnum} from '../entity';
import {environment} from '../../environments/environment';
import {LogService} from '../log';

declare const cordova: Cordova;

function _window(): any {
    // return the global native browser window object
    return window;
}

/**
 * Cordova Service
 */
@Injectable()
export class CordovaBackService {
    /**
     * taille Buffer d'écriture.
     */
    private static CSIZE: number = 4 * 1024 * 1024; // 4MB
    /**
     * Is Cordova.
     */
    private readonly _isCordova: boolean = false;
    /**
     * Is Android.
     */
    private readonly _isAndroid: boolean = false;
    /**
     * Is IOS.
     */
    private readonly _isIos: boolean = false;
    /**
     * Is Web.
     */
    private readonly _isWeb: boolean = false;
    /**
     * Is Web.
     */
    private readonly _haveCordovaCamera: boolean = false;
    /**
     * Is started
     */
    private _isStarted: boolean = false;
    /**
     * Device Id
     */
    private deviceId: string = '-404';
    /**
     * Synchronization Config.
     */
    private msaConfig: Config = {
        index: -1,
        tables: [],
        syncAuto: true,
    };
    /**
     * LogService
     * @type {null}
     */
    private _logService: LogService = null;

    /**
     * Get DataDirectory
     */
    public static getDataDirectory(): string {
        return cordova.file.dataDirectory;
    }

    /**
     * Get applicationDirectory
     */
    public static getApplicationDirectory(): string {
        return cordova.file.applicationDirectory;
    }

    /**
     * Convert Path
     * @param path path
     */
    public static convertPath(path: string): string {
        return _window().Ionic.WebView.convertFileSrc(path);
    }

    /**
     * Getter Cordova.
     */
    private static get cordova(): any {
        return _window().cordova;
    }

    /**
     * Constructor.
     */
    constructor(private configService: ConfigService) {
        this._isCordova = false;
        this._isAndroid = false;
        this._isIos = false;
        this._isWeb = false;
        if (!!_window().cordova) {
            this._isCordova = true;
            if (device != null) {
                if (device.platform.toUpperCase() === 'IOS') {
                    this._isIos = true;
                } else {
                    if (device.platform.toUpperCase() === 'ANDROID') {
                        this._isAndroid = true;
                    } else {
                        this._isWeb = true;
                    }
                }
            } else {
                this._isWeb = true;
            }
            if (navigator.camera != null) {
                this._haveCordovaCamera = true;
            }
        } else {
            this._isWeb = true;
        }
        this.initData();
    }

    /**
     * Init Data
     */
    private initData() {
        this._logService?.logLog('*** CordovaBackService - initData', 'CordovaBackService', 'initData', 2);
        this.configService.isReadyToStart().then(
            () => {
                this.configService.getConfig('DEVICE_ID')
                    .then(
                        (val) => {
                            if (val != null) {
                                // il faut continuer l'initialisation
                                this.deviceId = <string>val;
                            } else {
                                // il faut générer un device ID
                                if (this.onCordova && device != null && device.uuid != null && device.uuid.length > 0) {
                                    this.deviceId = device.uuid;
                                } else {
                                    this.deviceId = 'GEN_' + format(new Date(), 'yyyyMMdd_HHmmss') + '_' + UtilMpaFunction.uuid();
                                }
                                this.configService.saveConfig('DEVICE_ID', this.deviceId).catch(() => {
                                });
                            }
                            this._isStarted = true;
                        }
                    );
            },
            () => {
                // TODO gérer l'erreur
            }
        );
    }


    /**
     * Return a promise who resolve when it's ready to start.
     * @return {Promise<StatusEnum>}
     */
    public isReadyToStart(): Promise<StatusEnum> {
        this._logService?.logLog('*** CordovaBackService - isReadyToStart', 'CordovaBackService', 'isReadyToStart', 3);
        return new Promise(
            (resolve) => {
                this.isStartedTimeout(resolve);
            });
    }


    /**
     * is Started with a timeout
     * @param promiseResolve
     */
    private isStartedTimeout(promiseResolve: (r: StatusEnum) => void): void {
        this._logService?.logLog('*** CordovaBackService - isStartedTimeout', 'CordovaBackService', 'isStartedTimeout', 3);
        if (this._isStarted) {
            this._logService?.logLog('*** CordovaBackService - isStartedTimeout -- OK', 'CordovaBackService', 'isStartedTimeout', 3);
            promiseResolve(StatusEnum.OK);
        } else {
            this._logService?.logLog('*** CordovaBackService - isStartedTimeout -- TimeOut', 'CordovaBackService', 'isStartedTimeout', 3);
            setTimeout(this.isStartedTimeout.bind(this, promiseResolve), 100);
        }
    }

    /**
     * Get Device ID
     * @returns {string}
     */
    public getDeviceId(): string {
        return this.deviceId;
    }

    /**
     * Save MSA Config
     * @param {Config} config
     */
    public saveMsaConfig(config: Config): void {
        this.msaConfig = config;
    }

    /**
     * Get Info
     * @returns info
     */
    public geInfo(): any {
        const info: any = {
            id: this.deviceId,
            date: new Date(),
            mode: environment.mode,
            syncServerUrl: environment.syncServerUrl,
            syncApiKey: environment.syncApiKey,
            version: environment.version,
            name: environment.name,
            msaConfig: this.msaConfig
        };

        if (this.onCordova) {
            info.cordova = true;
            if (device != null) {
                info.deviceCordova = device.cordova;
                info.deviceCordova = device.cordova;
                info.deviceModel = device.model;
                info.devicePlatform = device.platform;
                info.deviceUuid = device.uuid;
                info.deviceVersion = device.version;
                info.deviceManufacturer = device.manufacturer;
            }
            if (navigator.connection != null && navigator.connection.type != null) {
                info.deviceManufacturer = navigator.connection.type;
            }
        } else {
            info.cordova = false;
            info.userAgent = navigator.userAgent;
            info.isOnline = navigator.onLine;
        }

        return info;
    }


    /**
     * If the plaftorm is on Cordova
     */
    public get onCordova(): boolean {
        return this._isCordova;
    }

    /**
     * Return if the device was connected to internet
     */
    public connectivityFunction(): Promise<boolean> {
        return new Promise(
            (resolve) => {
                if (this.onCordova) {
                    if (navigator.connection != null && navigator.connection.type != null) {
                        if ((navigator.connection.type !== Connection.NONE) && (navigator.connection.type !== Connection.UNKNOWN)) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                    } else {
                        resolve(navigator.onLine);
                    }
                } else {
                    resolve(navigator.onLine);
                }
            }
        );
    }

    /**
     * Save a file on the disk (need Cordova)
     * @param filePath filePath
     * @param blob Blob
     */
    public saveFile(filePath: string, blob: Blob): Promise<string> {
        return new Promise(
            (resolve, reject) => {
                if (this.onCordova && _window().resolveLocalFileSystemURL) {
                    const filePaths = filePath.substring(0, filePath.lastIndexOf('/')).split('/');
                    this.checkDirOrCreate(cordova.file.dataDirectory, filePaths).then(
                        () => {
                            this.writeFile(filePath, blob).then(
                                () => resolve('OK'),
                                () => {
                                    // TODO erreur Faire le rejet
                                    reject(false);
                                }
                            );
                        },
                        () => {
                            // TODO erreur Faire le rejet
                            reject(false);
                        }
                    );
                } else {
                    this._logService?.logError('-- CordovaBackService saveFile : Not Cordova Plugin File',
                        'CordovaBackService', 'saveFile', 1);
                    reject('Not Cordova Plugin File');
                }
            }
        );
    }

    /**
     * Donwload the file
     * @param filePath the file path
     * @param uri uri
     * @param sendInfoStream function for the progress Event
     */
    public downloadFile(filePath: string, uri: string, sendInfoStream: (loadedSize: number) => void): Promise<string> {
        return new Promise(
            (resolve, reject) => {
                if (this.onCordova && _window().resolveLocalFileSystemURL && FileTransfer != null) {
                    const filePaths = filePath.substring(0, filePath.lastIndexOf('/')).split('/');
                    const fileName: string = filePath.substring(filePath.lastIndexOf('/') + 1);
                    const subDirs: string = filePath.substring(0, filePath.lastIndexOf('/'));
                    this.checkDirOrCreate(cordova.file.dataDirectory, filePaths).then(
                        () => {
                            const fileTransfer: FileTransfer = new FileTransfer();
                            let timeStamp = Date.now();
                            fileTransfer.onprogress = (progressEvent) => {
                                // sendInfoStream(progressEvent.loaded);
                                if (Date.now() - timeStamp > 1000) {
                                    timeStamp = Date.now();
                                    sendInfoStream(progressEvent.loaded);
                                }
                            };
                            fileTransfer.download(
                                uri,
                                cordova.file.dataDirectory + '/tmpSynchro.file',
                                (fileEntry: FileEntry) => {
                                    this._logService?.logLog('-- CordovaBackService downloadFile download complete: ' + fileEntry.fullPath,
                                        'CordovaBackService', 'downloadFile', 3);
                                    _window().resolveLocalFileSystemURL(cordova.file.dataDirectory + '/' + subDirs,
                                        (dirEntry: DirectoryEntry) => {
                                            fileEntry.moveTo(dirEntry, fileName,
                                                () => {
                                                    resolve(filePath);
                                                    // resolve(fileEntry.fullPath);
                                                },
                                                (error) => {
                                                    this._logService?.logError('-- CordovaBackService downloadFile : Error moveTo FILEPATH '
                                                        + filePath + ' ERROR' + JSON.stringify(error),
                                                        'CordovaBackService', 'downloadFile', 1);
                                                    // TODO erreur Faire le rejet
                                                    reject(false);
                                                }
                                            );
                                        },
                                        (error: FileError) => {
                                            this._logService?.logError('-- CordovaBackService downloadFile : ' +
                                                'Error getFile Parent Path FILEPATH '
                                                + filePath + ' ERROR' + JSON.stringify(error),
                                                'CordovaBackService', 'downloadFile', 1);
                                            // TODO erreur Faire le rejet
                                            reject(false);
                                        }
                                    );
                                },
                                (error: FileTransferError) => {
                                    this._logService?.logError('-- CordovaBackService downloadFile : Error ' + JSON.stringify(error),
                                        'CordovaBackService', 'downloadFile', 1);
                                    this._logService?.logError('-- CordovaBackService downloadFile : Error source ' + error.source
                                        + ' target ' + error.target + ' code ' + error.code,
                                        'CordovaBackService', 'downloadFile', 1);
                                    reject('Error');
                                },
                                true,
                                {
                                    headers: {
                                        Accept: '*/*'
                                    }
                                }
                            );
                        },
                        () => {
                            // TODO erreur Faire le rejet
                            reject(false);
                        }
                    );
                } else {
                    this._logService?.logError('-- CordovaBackService downloadFile : Not Cordova Plugin File ',
                        'CordovaBackService', 'downloadFile', 1);
                    reject('Not Cordova Plugin File');
                }
            }
        );
    }


    /**
     * Copy the file
     * @param filePath the file path
     * @param uri uri
     */
    public copyFile(filePath: string, uri: string): Promise<string> {
        this._logService?.logLog('-- CordovaBackService copyFile --- FILEPATH ' + filePath + ' URI ' + uri,
            'CordovaBackService', 'copyFile', 3);
        return new Promise(
            (resolve, reject) => {
                if (this.onCordova && _window().resolveLocalFileSystemURL && FileTransfer != null) {
                    const filePaths = filePath.substring(0, filePath.lastIndexOf('/')).split('/');
                    const fileName: string = filePath.substring(filePath.lastIndexOf('/') + 1);
                    const subDirs: string = filePath.substring(0, filePath.lastIndexOf('/'));
                    this.checkDirOrCreate(cordova.file.dataDirectory, filePaths).then(
                        () => {
                            _window().resolveLocalFileSystemURL(cordova.file.dataDirectory + '/' + subDirs,
                                (dirEntry: DirectoryEntry) => {
                                    _window().resolveLocalFileSystemURL(uri,
                                        (fileEntry: DirectoryEntry) => {
                                            fileEntry.copyTo(dirEntry, fileName,
                                                () => {
                                                    resolve(filePath);
                                                    // resolve(fileEntry.fullPath);
                                                },
                                                (error) => {
                                                    this._logService?.logError('-- CordovaBackService copyFile : Error copyTo FILEPATH '
                                                        + filePath + ' ERROR ' + JSON.stringify(error),
                                                        'CordovaBackService', 'copyFile', 1);
                                                    // TODO erreur Faire le rejet
                                                    reject(false);
                                                }
                                            );
                                        },
                                        (error: FileError) => {
                                            this._logService?.logError('-- CordovaBackService copyFile : Error getFile Uri '
                                                + uri + ' ERROR ' + JSON.stringify(error),
                                                'CordovaBackService', 'copyFile', 1);
                                            // TODO erreur Faire le rejet
                                            reject(false);
                                        }
                                    );
                                },
                                (error: FileError) => {
                                    this._logService?.logError('-- CordovaBackService copyFile : Error Parent PATH '
                                        + filePath + ' ERROR ' + JSON.stringify(error),
                                        'CordovaBackService', 'copyFile', 1);
                                    // TODO erreur Faire le rejet
                                    reject(false);
                                }
                            );
                        },
                        () => {
                            // TODO erreur Faire le rejet
                            reject(false);
                        }
                    );
                } else {
                    this._logService?.logError('-- CordovaBackService copyFile : Not Cordova Plugin File ',
                        'CordovaBackService', 'copyFile', 1);
                    reject('Not Cordova Plugin File');
                }
            }
        );
    }


    public copyFileInDatabaseDirectory(filePath: string): Promise<string> {
        this._logService?.logLog('-- CordovaBackService copyFileInDatabaseDirectory --- FILEPATH ' + filePath,
            'CordovaBackService', 'copyFileInDatabaseDirectory', 3);
        return new Promise(
            (resolve, reject) => {
                if (this.onCordova && _window().resolveLocalFileSystemURL && FileTransfer != null) {
                    const fileName: string = filePath.substring(filePath.lastIndexOf('/') + 1);
                    let dataBaseDirectory: string = cordova.file.applicationStorageDirectory;
                    const prefixDataBaseDirectory: string = cordova.file.applicationStorageDirectory;
                    let suffixDataBaseDirectory: Array<string> = [];
                    if (this._isAndroid) {
                        dataBaseDirectory = cordova.file.applicationStorageDirectory + 'databases';
                        suffixDataBaseDirectory = ['databases'];
                    } else {
                        if (this._isIos) {
                            dataBaseDirectory = cordova.file.applicationStorageDirectory + 'Library/LocalDatabase';
                            suffixDataBaseDirectory = ['Library', 'LocalDatabase'];
                        }
                    }
                    this.checkDirOrCreate(prefixDataBaseDirectory, suffixDataBaseDirectory).then(
                        () => {
                            _window().resolveLocalFileSystemURL(dataBaseDirectory,
                                (dirEntry: DirectoryEntry) => {
                                    _window().resolveLocalFileSystemURL(cordova.file.dataDirectory + filePath,
                                        (fileEntry: DirectoryEntry) => {
                                            fileEntry.copyTo(dirEntry, fileName,
                                                () => {
                                                    resolve(filePath);
                                                },
                                                (error) => {
                                                    this._logService?.logError('-- CordovaBackService copyFileInDatabaseDirectory : ' +
                                                        'Error CopyTo PATH '
                                                        + filePath + ' ERROR ' + JSON.stringify(error),
                                                        'CordovaBackService', 'copyFileInDatabaseDirectory', 1);
                                                    // TODO erreur Faire le rejet
                                                    reject(false);
                                                }
                                            );
                                        },
                                        (error: FileError) => {
                                            this._logService?.logError('-- CordovaBackService copyFileInDatabaseDirectory : ' +
                                                'Error getFile Uri URI ' +
                                                cordova.file.dataDirectory + ' FILEPATH ' + filePath + ' ERROR ' + JSON.stringify(error),
                                                'CordovaBackService', 'copyFileInDatabaseDirectory', 1);
                                            // TODO erreur Faire le rejet
                                            reject(false);
                                        }
                                    );
                                },
                                (error: FileError) => {
                                    this._logService?.logError('-- CordovaBackService copyFileInDatabaseDirectory : ' +
                                        'Error getFile Parent DATABASE '
                                        + dataBaseDirectory + ' PATH ' + filePath + ' ERROR ' + JSON.stringify(error),
                                        'CordovaBackService', 'copyFileInDatabaseDirectory', 1);
                                    // TODO erreur Faire le rejet
                                    reject(false);
                                }
                            );
                        },
                        () => {
                            // TODO erreur Faire le rejet
                            reject(false);
                        }
                    );
                } else {
                    this._logService?.logError('-- CordovaBackService copyFileInDatabaseDirectory : Error Not Cordova Plugin File ',
                        'CordovaBackService', 'copyFileInDatabaseDirectory', 1);
                    reject('Not Cordova Plugin File');
                }
            }
        );
    }


    /**
     * Upload the file
     * @param filePath the file path
     * @param uri uri
     * @param sendInfoStream function for the progress Event
     */
    public uploadFile(filePath: string, uri: string, sendInfoStream: (loadedSize: number) => void): Promise<Journal> {
        return new Promise(
            (resolve, reject) => {
                if (this.onCordova && _window().resolveLocalFileSystemURL && FileTransfer != null) {
                    const fileTransfer: FileTransfer = new FileTransfer();
                    let timeStamp = Date.now();
                    fileTransfer.onprogress = (progressEvent) => {
                        // sendInfoStream(progressEvent.loaded);
                        if (Date.now() - timeStamp > 1000) {
                            timeStamp = Date.now();
                            sendInfoStream(progressEvent.loaded);
                        }
                    };
                    fileTransfer.upload(
                        cordova.file.dataDirectory + filePath,
                        uri,
                        (result: FileUploadResult) => {
                            this._logService?.logLog('-- CordovaBackService uploadFile --- OK ' + JSON.stringify(result),
                                'CordovaBackService', 'uploadFile', 3);
                            if (result.responseCode === 200) {
                                const succ: any = JSON.parse(result.response);
                                resolve(succ);
                            } else {
                                this._logService?.logLog('-- CordovaBackService uploadFile --- download error code ' + result.responseCode,
                                    'CordovaBackService', 'uploadFile', 3);
                                reject();
                            }
                        },
                        (error: FileTransferError) => {
                            this._logService?.logError('-- CordovaBackService uploadFile : Error error ' + JSON.stringify(error),
                                'CordovaBackService', 'uploadFile', 1);
                            this._logService?.logError('-- CordovaBackService uploadFile : Error error SOURCE' + error.source +
                                ' TARGET ' + error.target + ' CODE ' + error.code,
                                'CordovaBackService', 'uploadFile', 1);
                            reject();
                        }
                    );
                } else {
                    this._logService?.logError('-- CordovaBackService uploadFile : Not Cordova Plugin File',
                        'CordovaBackService', 'uploadFile', 1);
                    reject('Not Cordova Plugin File');
                }
            }
        );
    }


    /**
     * Upload the file
     * @param journal File Journal
     * @param uri uri
     * @param sendInfoStream function for the progress Event
     */
    public uploadFileV2(journal: FileJournal, uri: string, sendInfoStream: (loadedSize: number) => void): Promise<Journal> {
        return new Promise(
            (resolve, reject) => {
                if (this.onCordova && _window().resolveLocalFileSystemURL && FileTransfer != null) {
                    const fileTransfer: FileTransfer = new FileTransfer();
                    let timeStamp = Date.now();
                    fileTransfer.onprogress = (progressEvent) => {
                        // sendInfoStream(progressEvent.loaded);
                        if (Date.now() - timeStamp > 1000) {
                            timeStamp = Date.now();
                            sendInfoStream(progressEvent.loaded);
                        }
                    };
                    fileTransfer.upload(
                        cordova.file.dataDirectory + journal.file_path,
                        uri,
                        (result: FileUploadResult) => {
                            this._logService?.logLog('-- CordovaBackService uploadFileV2 --- OK ' + JSON.stringify(result),
                                'CordovaBackService', 'uploadFileV2', 3);
                            if (result.responseCode === 200) {
                                const succ: any = JSON.parse(result.response);
                                resolve(succ);
                            } else {
                                this._logService?.logLog('-- CordovaBackService uploadFileV2 --- download error code '
                                    + result.responseCode,
                                    'CordovaBackService', 'uploadFileV2', 3);
                                reject();
                            }
                        },
                        (error: FileTransferError) => {
                            this._logService?.logError('-- CordovaBackService uploadFileV2 : Error error ' + JSON.stringify(error),
                                'CordovaBackService', 'uploadFileV2', 1);
                            this._logService?.logError('-- CordovaBackService uploadFileV2 : Error error SOURCE' + error.source +
                                ' TARGET ' + error.target + ' CODE ' + error.code,
                                'CordovaBackService', 'uploadFileV2', 1);
                            reject();
                        },
                        {
                            params: {
                                journal: '[' + JSON.stringify(journal) + ']'
                            }
                        }
                    );
                } else {
                    this._logService?.logError('-- CordovaBackService uploadFileV2 : Not Cordova Plugin File',
                        'CordovaBackService', 'uploadFileV2', 1);
                    reject('Not Cordova Plugin File');
                }
            }
        );
    }



    /**
     * Upload the file
     * @param journal File Journal
     * @param uri uri
     * @param params params
     */
    public uploadFileV3(journal: FileData, uri: string, params: any): Promise<Journal> {
        return new Promise(
            (resolve, reject) => {
                if (this.onCordova && _window().resolveLocalFileSystemURL && FileTransfer != null) {
                    const fileTransfer: FileTransfer = new FileTransfer();
                    fileTransfer.upload(
                        cordova.file.dataDirectory + journal.file_path,
                        uri,
                        (result: FileUploadResult) => {
                            this._logService?.logLog('-- CordovaBackService uploadFileV3 --- OK ' + JSON.stringify(result),
                                'CordovaBackService', 'uploadFileV3', 3);
                            if (result.responseCode === 200) {
                                const succ: any = JSON.parse(result.response);
                                resolve(succ);
                            } else {
                                this._logService?.logLog('-- CordovaBackService uploadFileV3 --- download error code '
                                    + result.responseCode,
                                    'CordovaBackService', 'uploadFileV3', 3);
                                reject();
                            }
                        },
                        (error: FileTransferError) => {
                            this._logService?.logError('-- CordovaBackService uploadFileV3 : Error error ' + JSON.stringify(error),
                                'CordovaBackService', 'uploadFileV3', 1);
                            this._logService?.logError('-- CordovaBackService uploadFileV3 : Error error SOURCE' + error.source +
                                ' TARGET ' + error.target + ' CODE ' + error.code,
                                'CordovaBackService', 'uploadFileV3', 1);
                            reject();
                        },
                        {
                            params
                        }
                    );
                } else {
                    this._logService?.logError('-- CordovaBackService uploadFileV3 : Not Cordova Plugin File',
                        'CordovaBackService', 'uploadFileV3', 1);
                    reject('Not Cordova Plugin File');
                }
            }
        );
    }




    /**
     * Write a file on the disk
     * @param filePath path
     * @param blob the binary to write
     */
    private writeFile(filePath: string, blob: Blob): Promise<string> {
        return new Promise(
            (resolve, reject) => {
                // filePath.split('/');
                const fileName: string = filePath.substring(filePath.lastIndexOf('/') + 1);
                const subDirs: string = filePath.substring(0, filePath.lastIndexOf('/'));
                this._logService?.logLog('-- CordovaBackService writeFile --- FILENAME ' + fileName + ' SUBDIRS ' + subDirs,
                    'CordovaBackService', 'writeFile', 3);
                this.writeTmp(blob, true).then(
                    () => {
                        _window().resolveLocalFileSystemURL(cordova.file.dataDirectory + '/tmpSynchro.file',
                            (tmpFileEntry: DirectoryEntry) => {
                                _window().resolveLocalFileSystemURL(cordova.file.dataDirectory + '/' + subDirs,
                                    (dirEntry: DirectoryEntry) => {
                                        tmpFileEntry.moveTo(dirEntry, fileName,
                                            () => {
                                                resolve('OK');
                                            },
                                            (error: FileError) => {
                                                this._logService?.logError('-- CordovaBackService writeFile : Error moveTo FILEPATH '
                                                    + filePath + ' ERROR ' + JSON.stringify(error),
                                                    'CordovaBackService', 'writeFile', 1);
                                                // TODO erreur Faire le rejet
                                                reject(false);
                                            }
                                        );
                                    },
                                    (error: FileError) => {
                                        this._logService?.logError('-- CordovaBackService writeFile : Error getFile Parent FILEPATH '
                                            + filePath + ' ERROR ' + JSON.stringify(error),
                                            'CordovaBackService', 'writeFile', 1);
                                        // TODO erreur Faire le rejet
                                        reject(false);
                                    }
                                );
                            },
                            (error: FileError) => {
                                this._logService?.logError('-- CordovaBackService writeFile : Error getFile TmpPath FILEPATH '
                                    + filePath + ' ERROR ' + JSON.stringify(error),
                                    'CordovaBackService', 'writeFile', 1);
                                // TODO erreur Faire le rejet
                                reject(false);
                            }
                        );
                    },
                    () => {
                        // TODO erreur Faire le rejet
                        reject(false);
                    }
                );
            }
        );
    }

    /**
     * Read a file on file system
     * @param {string} filePath
     * @returns {Promise<ArrayBuffer>}
     */
    public readBinaryFile(filePath: string): Promise<ArrayBuffer> {
        return new Promise(
            (resolve, reject) => {
                _window().resolveLocalFileSystemURL(cordova.file.dataDirectory + filePath,
                    (fileEntry: DirectoryEntry) => {
                        const fileReader: FileReader = new FileReader();
                        fileReader.onload = () => {
                            const arrayBuffer: ArrayBuffer = <ArrayBuffer>fileReader.result;
                            resolve(arrayBuffer);
                        };
                        fileReader.readAsArrayBuffer(fileEntry as any);
                    },
                    () => {
                        reject();
                    });
            });
    }

    /**
     * Write Tmp file
     * @param blob the binary to write
     * @param isNew  is a new file
     */
    private writeTmp(blob: Blob, isNew: boolean): Promise<boolean> {
        return new Promise(
            (resolve, reject) => {
                if (blob.size === 0) {
                    resolve(true);
                } else {
                    let flag = {};
                    if (isNew) {
                        flag = {create: true};
                    }
                    _window().resolveLocalFileSystemURL(cordova.file.dataDirectory,
                        (dirEntry: DirectoryEntry) => {
                            dirEntry.getFile('tmpSynchro.file',
                                flag,
                                (fileEntry) => {
                                    fileEntry.createWriter((fileWriter) => {
                                        fileWriter.onwriteend = () => {
                                            this.writeTmp(blob.slice(CordovaBackService.CSIZE), false).then(
                                                () => resolve(true),
                                                () => {
                                                    // TODO erreur Faire le rejet
                                                    reject(false);
                                                }
                                            );
                                        };
                                        fileWriter.onerror = (e) => {
                                            this._logService?.logError('-- CordovaBackService writeTmp : Error Failed file write '
                                                + JSON.stringify(e),
                                                'CordovaBackService', 'writeTmp', 1);
                                            reject('KO');
                                        };
                                        fileWriter.seek(fileWriter.length); // Start write position at EOF.
                                        fileWriter.write(blob.slice(0, CordovaBackService.CSIZE));
                                    });
                                },
                                (error: FileError) => {
                                    this._logService?.logError('-- CordovaBackService writeTmp : Error getFile Parent Path '
                                        + JSON.stringify(error),
                                        'CordovaBackService', 'writeTmp', 1);
                                    // TODO erreur Faire le rejet
                                    reject(false);
                                }
                            );
                        },
                        (error: FileError) => {
                            this._logService?.logError('-- CordovaBackService writeTmp : ErrorResolverLocal Parent Path '
                                + JSON.stringify(error),
                                'CordovaBackService', 'writeTmp', 1);
                            // TODO erreur Faire le rejet
                            reject(false);
                        }
                    );
                }
            }
        );
    }

    /**
     * Create the directories hierarchy
     * @param parentPath parentPath
     * @param childrenPaths sub path
     */
    private checkDirOrCreate(parentPath: string, childrenPaths: Array<string>): Promise<boolean> {
        return new Promise(
            (resolve, reject) => {
                const childPath = childrenPaths.shift();
                if (childPath != null) {
                    if (childPath.trim().length > 0) {
                        _window().resolveLocalFileSystemURL(parentPath,
                            (fileEntry: DirectoryEntry) => {
                                fileEntry.getDirectory(childPath,
                                    {create: true},
                                    () => {
                                        this.checkDirOrCreate(parentPath + '/' + childPath, childrenPaths).then(
                                            () => {
                                                resolve(true);
                                            },
                                            () => {
                                                // TODO erreur Faire le rejet
                                                reject(false);
                                            }
                                        );
                                    },
                                    (error: FileError) => {
                                        this._logService?.logError('-- CordovaBackService checkDirOrCreate : ' +
                                            'ErrorResolverLocal Parent Path 1 PATH'
                                            + parentPath + ' ERROR ' + JSON.stringify(error),
                                            'CordovaBackService', 'checkDirOrCreate', 1);
                                        // TODO erreur Faire le rejet
                                        reject(false);
                                    }
                                );
                            },
                            (error: FileError) => {
                                this._logService?.logError('-- CordovaBackService checkDirOrCreate : ErrorResolverLocal Parent Path 2 PATH'
                                    + parentPath + ' ERROR ' + JSON.stringify(error),
                                    'CordovaBackService', 'checkDirOrCreate', 1);
                                // TODO erreur Faire le rejet
                                reject(false);
                            }
                        );


                    } else {
                        this.checkDirOrCreate(parentPath, childrenPaths).then(
                            () => {
                                resolve(true);
                            },
                            () => {
                                // TODO erreur Faire le rejet
                                reject(false);
                            }
                        );
                    }
                } else {
                    resolve(true);
                }
            }
        );
    }


    /**
     * Take a picture
     * @param filePath file Path
     * @param viaCamera with Camera
     * @param viaLibrary with Library
     * @param viaAlbum with Album
     * @param cameraFront with the camera Front
     * @param targetWidth width of the target picture
     * @param targetHeight height of the target picture
     */
    public takePicture(
        filePath: string,
        viaCamera: boolean,
        viaLibrary: boolean,
        viaAlbum: boolean,
        cameraFront: boolean,
        targetWidth?: number,
        targetHeight?: number
    ): Promise<FileData> {
        return new Promise(
            (resolve, reject) => {
                if (this._isCordova && this._haveCordovaCamera) {
                    const cameraOption: CameraOptions = {
                        quality: 100,
                        destinationType: Camera.DestinationType.FILE_URI,
                        sourceType: Camera.PictureSourceType.CAMERA,
                        allowEdit: true,
                        encodingType: Camera.EncodingType.JPEG,
                        mediaType: Camera.MediaType.PICTURE,
                        correctOrientation: true,
                        saveToPhotoAlbum: false,
                        cameraDirection: Camera.Direction.BACK,
                    };
                    if (viaAlbum) {
                        cameraOption.sourceType = Camera.PictureSourceType.SAVEDPHOTOALBUM;
                    } else {
                        if (viaLibrary) {
                            cameraOption.sourceType = Camera.PictureSourceType.PHOTOLIBRARY;
                        }
                    }
                    if (this._isAndroid) {
                        cameraOption.allowEdit = false;
                    }
                    if (targetHeight != null && targetWidth != null) {
                        //cameraOption.allowEdit = true;
                        cameraOption.targetHeight = targetHeight;
                        cameraOption.targetWidth = targetWidth;
                    }
                    if (cameraFront) {
                        cameraOption.cameraDirection = Camera.Direction.FRONT;
                    }

                    navigator.camera.getPicture(
                        (pictureData) => {
                            this._logService?.logLog('-- CordovaBackService takePicture --- PICTURE ' + pictureData,
                                'CordovaBackService', 'takePicture', 3);
                            const filePaths = filePath.substring(0, filePath.lastIndexOf('/')).split('/');
                            const fileName: string = filePath.substring(filePath.lastIndexOf('/') + 1);
                            const subDirs: string = filePath.substring(0, filePath.lastIndexOf('/'));
                            const fileData: FileData = {
                                path: filePath,
                                file_origin: FileOrigin.LOCAL,
                                file_upload_status: FileUploadStatus.UNSAVED,
                                file_status: FileStatus.OK,
                                file_type: FileType.FILE,
                                size: -1,
                                hash: '42',
                                file_path: filePath
                            };
                            this.checkDirOrCreate(cordova.file.dataDirectory, filePaths).then(
                                () => {
                                    _window().resolveLocalFileSystemURL(pictureData,
                                        (tmpFileEntry: DirectoryEntry) => {
                                            fileData.size = (tmpFileEntry as any).file.size;
                                            _window().resolveLocalFileSystemURL(cordova.file.dataDirectory + subDirs,
                                                (dirEntry: DirectoryEntry) => {
                                                    if (viaAlbum || viaLibrary) {
                                                        // Copy
                                                        tmpFileEntry.copyTo(dirEntry, fileName,
                                                            () => {
                                                                resolve(fileData);
                                                            },
                                                            (error: FileError) => {
                                                                this._logService?.logError('-- CordovaBackService takePicture : ' +
                                                                    'Error copyTo PATH'
                                                                    + filePath + ' ERROR ' + JSON.stringify(error),
                                                                    'CordovaBackService', 'takePicture', 1);
                                                                // TODO erreur Faire le rejet
                                                                reject(false);
                                                            }
                                                        );
                                                    } else {
                                                        // Move
                                                        tmpFileEntry.moveTo(dirEntry, fileName,
                                                            () => {
                                                                resolve(fileData);
                                                            },
                                                            (error: FileError) => {
                                                                this._logService?.logError('-- CordovaBackService takePicture : ' +
                                                                    'Error moveTo PATH'
                                                                    + filePath + ' ERROR ' + JSON.stringify(error),
                                                                    'CordovaBackService', 'takePicture', 1);
                                                                // TODO erreur Faire le rejet
                                                                reject(false);
                                                            }
                                                        );
                                                    }
                                                },
                                                (error: FileError) => {
                                                    this._logService?.logError('-- CordovaBackService takePicture : ' +
                                                        'Error getFile Parent Path PATH'
                                                        + filePath + ' ERROR ' + JSON.stringify(error),
                                                        'CordovaBackService', 'takePicture', 1);
                                                    // TODO erreur Faire le rejet
                                                    reject(false);
                                                }
                                            );
                                        },
                                        (error: FileError) => {
                                            this._logService?.logError('-- CordovaBackService takePicture : Error getFile TmpPath PATH'
                                                + filePath + ' ERROR ' + JSON.stringify(error),
                                                'CordovaBackService', 'takePicture', 1);
                                            // TODO erreur Faire le rejet
                                            reject(false);
                                        }
                                    );
                                },
                                () => {
                                    this._logService?.logError('-- CordovaBackService takePicture : Error checkDirOrCreate PATH'
                                        + filePath,
                                        'CordovaBackService', 'takePicture', 1);
                                    reject(false);
                                }
                            );
                        },
                        (error) => {
                            this._logService?.logError('-- CordovaBackService takePicture : Error Error PATH'
                                + filePath + ' ERROR ' + JSON.stringify(error),
                                'CordovaBackService', 'takePicture', 1);
                            reject();
                        },
                        cameraOption
                    );
                } else {
                    this._logService?.logError('-- CordovaBackService takePicture : Environnement Non Cordova',
                        'CordovaBackService', 'takePicture', 1);
                    reject();
                }
            }
        );
    }


    /**
     * Read QrCode
     * @param {boolean} frontCamera if true, use the front camera
     * @param {boolean} torchOn if true, the torch is on
     * @param {string} prompt label for Android
     * @returns {Promise<string>} return readden qrcode
     */
    public readQRCode(frontCamera: boolean, torchOn: boolean, prompt: string): Promise<string> {
        return new Promise<string>(
            (resolve, reject) => {
                // @ts-ignore
                if (cordova != null && cordova.plugins != null && cordova.plugins.barcodeScanner != null) {
                    // @ts-ignore
                    cordova.plugins.barcodeScanner.scan(
                        (result: any) => {
                            if (!result.cancelled) {
                                resolve(result.text);
                            } else {
                                reject();
                            }
                        },
                        (error: any) => {
                            this._logService?.logError('-- CordovaBackService takeQRCode : Error  '
                                + ' ERROR ' + JSON.stringify(error),
                                'CordovaBackService', 'takeQRCode', 1);
                            reject(error);
                        },
                        {
                            preferFrontCamera: false, // iOS and Android
                            showFlipCameraButton: true, // iOS and Android
                            showTorchButton: true, // iOS and Android
                            torchOn: false, // Android, launch with the torch switched on (if available)
                            saveHistory: true, // Android, save scan history (default false)
                            prompt, // Android
                            resultDisplayDuration: 500, // Android, display scanned text for X ms. 0 suppresses it entirely, default 1500
                            formats: 'QR_CODE', // default: all but PDF_417 and RSS_EXPANDED
                            // orientation: 'portrait', // Android only (portrait|landscape), default unset so it rotates with the device
                            disableAnimations: true, // iOS
                            disableSuccessBeep: false // iOS and Android
                        }
                    );
                } else {
                    this._logService?.logError('-- CordovaBackService takeQRCode : Error No Plugins  QRScanner',
                        'CordovaBackService', 'takeQRCode', 1);
                    // here we can handle errors and clean up any loose ends.
                    reject();
                }
            }
        );
    }

    /**
     * Getter.
     */
    public get haveCordovaCamera(): boolean {
        return this._haveCordovaCamera;
    }

    /**
     * Move the file with a pop Parent
     * For instance, /tmpMo/toto/titi.pdf => /toto/titi.pdf
     * @param filePath
     */
    public moveFilePopParent(filePath: string): Promise<string> {
        return new Promise(
            (resolve, reject) => {
                const subDirs: string = filePath.substring(0, filePath.lastIndexOf('/'))
                    .substring(filePath.indexOf('/') + 1);
                const filePaths = subDirs.split('/');
                const fileName: string = filePath.substring(filePath.lastIndexOf('/') + 1);

                this.checkDirOrCreate(cordova.file.dataDirectory, filePaths).then(
                    () => {
                        _window().resolveLocalFileSystemURL(cordova.file.dataDirectory + filePath,
                            (tmpFileEntry: DirectoryEntry) => {
                                _window().resolveLocalFileSystemURL(cordova.file.dataDirectory + subDirs,
                                    (dirEntry: DirectoryEntry) => {
                                        // Move
                                        tmpFileEntry.moveTo(dirEntry, fileName,
                                            () => {
                                                resolve(subDirs + '/' + fileName);
                                            },
                                            (error: FileError) => {
                                                this._logService?.logError('-- CordovaBackService moveFilePopParent : Error moveTo PATH'
                                                    + filePath + ' ERROR ' + JSON.stringify(error),
                                                    'CordovaBackService', 'moveFilePopParent', 1);
                                                // TODO erreur Faire le rejet
                                                reject(false);
                                            }
                                        );
                                    },
                                    (error: FileError) => {
                                        this._logService?.logError('-- CordovaBackService moveFilePopParent : Error getFile Parent PATH'
                                            + filePath + ' ERROR ' + JSON.stringify(error),
                                            'CordovaBackService', 'moveFilePopParent', 1);
                                        // TODO erreur Faire le rejet
                                        reject(false);
                                    }
                                );
                            },
                            (error: FileError) => {
                                this._logService?.logError('-- CordovaBackService moveFilePopParent : Error getFile TmpPath PATH'
                                    + filePath + ' ERROR ' + JSON.stringify(error),
                                    'CordovaBackService', 'moveFilePopParent', 1);
                                // TODO erreur Faire le rejet
                                reject(false);
                            }
                        );
                    },
                    () => {
                        this._logService?.logError('-- CordovaBackService moveFilePopParent : Error checkDirOrCreate Error PATH'
                            + filePath,
                            'CordovaBackService', 'moveFilePopParent', 1);
                        reject(false);
                    }
                );
            }
        );
    }


    /**
     * Quit the cordova application or close the frame.
     */
    public quit(): void {
        if ((<any>navigator).app) {
            (<any>navigator).app.exitApp();
        } else {
            if ((<any>navigator).device) {
                (<any>navigator).device.exitApp();
            } else {
                window.close();
            }
        }
    }

    /**
     * Setter
     * @param {LogService} value
     */
    public set logService(value: LogService) {
        this._logService = value;
    }

    /**
     * Getter Cordova
     */
    public get isCordova(): boolean {
        return this._isCordova;
    }
    /**
     * Getter Android
     */
    public get isAndroid(): boolean {
        return this._isAndroid;
    }
    /**
     * Getter Ios
     */
    public get isIos(): boolean {
        return this._isIos;
    }
    /**
     * Getter Web
     */
    public get isWeb(): boolean {
        return this._isWeb;
    }
}
